<template>
  <div class="lf-bulk">
    <div class="container-fluid py-4">

      <!-- Header -->
      <header class="lf-bulk__header">
        <div>
          <h1>Email Marketing</h1>
          <p>Envío masivo seguro · LaFuerza IT</p>
        </div>
        <div class="lf-bulk__stats">
          <div class="lf-stat">
            <span class="lf-stat__val">{{ templates.length }}</span>
            <span class="lf-stat__label">Plantillas</span>
          </div>
          <div class="lf-stat">
            <span class="lf-stat__val">{{ contacts.length }}</span>
            <span class="lf-stat__label">Contactos</span>
          </div>
          <div class="lf-stat">
            <span class="lf-stat__val" :class="smtpStatus ? 'is-ok' : 'is-err'">
              {{ smtpStatus ? 'OK' : 'Off' }}
            </span>
            <span class="lf-stat__label">SMTP</span>
          </div>
        </div>
      </header>

      <div class="row g-4">
        <!-- LEFT -->
        <div class="col-lg-8">

          <!-- Steps nav -->
          <nav class="lf-steps">
            <button
              v-for="step in steps"
              :key="step.id"
              class="lf-steps__btn"
              :class="{ 'is-active': currentStep === step.id }"
              :disabled="step.id > 1 && !canGoToStep(step.id)"
              @click="goToStep(step.id)"
            >
              <span class="lf-steps__num">{{ step.id }}</span>
              <span>{{ step.label }}</span>
            </button>
          </nav>

          <!-- STEP 1: Contacts -->
          <section v-if="currentStep === 1" class="lf-panel">
            <h2 class="lf-panel__title">Cargar contactos</h2>

            <div class="row g-3">
              <!-- CSV -->
              <div class="col-md-6">
                <div class="lf-card-sm">
                  <h3>Subir CSV</h3>
                  <p class="lf-hint">Formato: nombre, email, empresa, teléfono</p>
                  <input
                    v-model="tempSecurityKey"
                    type="password"
                    class="lf-input"
                    placeholder="Clave para subir CSV"
                  >
                  <input
                    ref="csvInput"
                    type="file"
                    accept=".csv"
                    class="lf-input lf-input--file"
                    @change="handleCSVUpload"
                  >
                  <button
                    class="lf-btn lf-btn--ghost lf-btn--full"
                    :disabled="uploading || !tempSecurityKey"
                    @click="triggerCSVUpload"
                  >
                    {{ uploading ? 'Subiendo...' : 'Subir CSV' }}
                  </button>
                </div>
              </div>

              <!-- Paste -->
              <div class="col-md-6">
                <div class="lf-card-sm">
                  <h3>Pegar emails</h3>
                  <p class="lf-hint">Un email por línea</p>
                  <input
                    v-model="tempSecurityKey"
                    type="password"
                    class="lf-input"
                    placeholder="Clave para procesar"
                  >
                  <textarea
                    v-model="emailText"
                    class="lf-input"
                    rows="4"
                    placeholder="ejemplo@email.com"
                  ></textarea>
                  <button
                    class="lf-btn lf-btn--ghost lf-btn--full"
                    :disabled="!emailText.trim() || !tempSecurityKey"
                    @click="processEmailText"
                  >
                    Procesar
                  </button>
                </div>
              </div>
            </div>

            <!-- Lista -->
            <div v-if="contacts.length" class="lf-panel__section">
              <div class="lf-panel__subhead">
                <h3>Contactos cargados ({{ contacts.length }})</h3>
                <button class="lf-btn lf-btn--danger lf-btn--sm" @click="askClearContacts">
                  Limpiar
                </button>
              </div>

              <div class="lf-table-wrap">
                <table class="lf-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Nombre</th>
                      <th>Email</th>
                      <th>Empresa</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(c, i) in displayedContacts" :key="i">
                      <td>{{ (currentPage - 1) * pageSize + i + 1 }}</td>
                      <td>{{ c.nombre || '—' }}</td>
                      <td><code>{{ c.email }}</code></td>
                      <td>{{ c.empresa || '—' }}</td>
                      <td>
                        <span class="lf-dot" :class="c.isValid ? 'is-ok' : 'is-err'"></span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-if="totalPages > 1" class="lf-pagination">
                <button :disabled="currentPage === 1" @click="prevPage">«</button>
                <button
                  v-for="p in totalPages"
                  :key="p"
                  :class="{ 'is-active': p === currentPage }"
                  @click="goToPage(p)"
                >{{ p }}</button>
                <button :disabled="currentPage === totalPages" @click="nextPage">»</button>
              </div>

              <div class="lf-panel__actions">
                <button class="lf-btn lf-btn--primary" @click="nextStep">
                  Continuar
                </button>
              </div>
            </div>

            <div v-else class="lf-empty">
              <p>Sin contactos cargados.</p>
              <p class="lf-hint">Ingresa la clave de seguridad para comenzar.</p>
            </div>
          </section>

          <!-- STEP 2: Templates -->
          <section v-if="currentStep === 2" class="lf-panel">
            <h2 class="lf-panel__title">Seleccionar plantilla</h2>

            <div class="lf-filters">
              <button
                v-for="cat in templateCategories"
                :key="cat.id"
                class="lf-chip"
                :class="{ 'is-active': selectedCategory === cat.id }"
                @click="selectedCategory = cat.id"
              >
                {{ cat.name }}
                <span class="lf-chip__count">{{ getCategoryCount(cat.id) }}</span>
              </button>
            </div>

            <input
              v-model="templateSearch"
              class="lf-input"
              type="search"
              placeholder="Buscar por nombre o descripción..."
            >

            <div v-if="!filteredTemplates.length" class="lf-empty">
              <p>No se encontraron plantillas.</p>
              <button class="lf-btn lf-btn--ghost lf-btn--sm" @click="clearFilters">
                Limpiar filtros
              </button>
            </div>

            <div v-else class="lf-templates">
              <article
                v-for="t in filteredTemplates"
                :key="t.id"
                class="lf-template"
                :class="{ 'is-selected': selectedTemplate === t.id }"
                @click="selectTemplate(t.id)"
              >
                <header>
                  <h4>{{ t.name }}</h4>
                  <span v-if="selectedTemplate === t.id" class="lf-template__check">✓</span>
                </header>
                <p>{{ t.description }}</p>
                <p class="lf-template__subject">
                  <strong>Asunto:</strong> {{ t.subject }}
                </p>
                <button class="lf-btn lf-btn--ghost lf-btn--sm" @click.stop="previewTemplate(t.id)">
                  Vista previa
                </button>
              </article>
            </div>

            <div class="lf-panel__actions lf-panel__actions--split">
              <button class="lf-btn lf-btn--ghost" @click="prevStep">Volver</button>
              <button class="lf-btn lf-btn--primary" :disabled="!selectedTemplate" @click="nextStep">
                Continuar
              </button>
            </div>
          </section>

          <!-- STEP 3: Config -->
          <section v-if="currentStep === 3" class="lf-panel">
            <h2 class="lf-panel__title">Configurar envío</h2>

            <div class="lf-summary">
              <strong>{{ selectedTemplateName }}</strong>
              <small>{{ defaultSubject }}</small>
            </div>

            <label class="lf-field">
              <span>Asunto del email</span>
              <input v-model="emailSubject" :placeholder="defaultSubject" class="lf-input">
              <small class="lf-hint">{{ emailSubject.length }} caracteres (recomendado: &lt; 70)</small>
            </label>

            <label class="lf-field">
              <span>Mensaje personalizado (opcional)</span>
              <textarea
                v-model="customMessage"
                class="lf-input"
                rows="3"
                placeholder="Variables: {nombre}, {empresa}, {telefono}, {fecha}"
              ></textarea>
            </label>

            <div class="lf-row-2">
              <label class="lf-field">
                <span>Delay entre emails (ms)</span>
                <input v-model.number="sendDelay" type="number" min="1000" max="10000" step="500" class="lf-input">
              </label>
              <label class="lf-field">
                <span>Tamaño de lote</span>
                <input v-model.number="batchSize" type="number" min="1" max="50" class="lf-input">
              </label>
            </div>

            <label class="lf-checkbox">
              <input v-model="skipDuplicateCheck" type="checkbox">
              <span>Omitir verificación de duplicados</span>
            </label>

            <div class="lf-warn">
              <strong>Clave de seguridad</strong>
              <p class="lf-hint">
                Requerida para enviar. Debe coincidir con <code>EMAIL_SECURITY_KEY</code> del servidor.
              </p>
              <div class="lf-input-group">
                <input
                  v-model="securityKey"
                  :type="showSecurityKey ? 'text' : 'password'"
                  class="lf-input"
                  placeholder="Ingresa la clave"
                >
                <button class="lf-btn lf-btn--ghost lf-btn--icon" @click="showSecurityKey = !showSecurityKey">
                  {{ showSecurityKey ? 'Ocultar' : 'Ver' }}
                </button>
              </div>
            </div>

            <div class="lf-panel__actions lf-panel__actions--split">
              <button class="lf-btn lf-btn--ghost" @click="prevStep">Volver</button>
              <div class="lf-actions-right">
                <button class="lf-btn lf-btn--ghost" :disabled="!securityKey" @click="sendTestEmail">
                  Probar
                </button>
                <button
                  class="lf-btn lf-btn--primary"
                  :disabled="!emailSubject || !securityKey"
                  @click="nextStep"
                >
                  Continuar
                </button>
              </div>
            </div>
          </section>

          <!-- STEP 4: Send -->
          <section v-if="currentStep === 4" class="lf-panel">
            <h2 class="lf-panel__title">Enviar campaña</h2>

            <div class="lf-kpis">
              <div><strong>{{ contacts.length }}</strong><span>Contactos</span></div>
              <div><strong>{{ validContactsCount }}</strong><span>Válidos</span></div>
              <div><strong>{{ selectedTemplateName || '—' }}</strong><span>Plantilla</span></div>
              <div><strong>{{ estimatedTime }}</strong><span>Tiempo est.</span></div>
            </div>

            <div v-if="sending" class="lf-progress">
              <div class="lf-progress__bar" :style="{ width: progress + '%' }"></div>
              <p>{{ Math.round(progress) }}% · {{ sendingStats.successful }} ok · {{ sendingStats.failed }} fallidos</p>
            </div>

            <div v-if="campaignResults" class="lf-success">
              <strong>Campaña enviada</strong>
              <p>{{ campaignResults.successful }} enviados · {{ campaignResults.failed }} fallidos</p>
            </div>

            <div class="lf-panel__actions lf-panel__actions--split">
              <button class="lf-btn lf-btn--ghost" @click="prevStep">Volver</button>
              <button
                v-if="!campaignResults"
                class="lf-btn lf-btn--primary"
                :disabled="!securityKey || sending"
                @click="askStartSending"
              >
                {{ sending ? 'Enviando...' : 'Iniciar envío masivo' }}
              </button>
              <button v-else class="lf-btn lf-btn--primary" @click="askStartNewCampaign">
                Nueva campaña
              </button>
            </div>
          </section>
        </div>

        <!-- RIGHT -->
        <aside class="col-lg-4">
          <div class="lf-side">
            <h3>Acciones rápidas</h3>
            <button class="lf-btn lf-btn--ghost lf-btn--full" @click="downloadTemplate">
              Descargar template CSV
            </button>
            <button class="lf-btn lf-btn--ghost lf-btn--full" :disabled="!securityKey" @click="testSMTPConnection">
              Probar SMTP
            </button>
            <button class="lf-btn lf-btn--ghost lf-btn--full" :disabled="!securityKey" @click="checkDuplicates">
              Verificar duplicados
            </button>
          </div>

          <div class="lf-side">
            <h3>Resumen</h3>
            <ul class="lf-side__list">
              <li><span>Contactos</span><strong>{{ contacts.length }}</strong></li>
              <li><span>Válidos</span><strong>{{ validContactsCount }}</strong></li>
              <li><span>Plantilla</span><strong>{{ selectedTemplateName || '—' }}</strong></li>
              <li><span>Clave</span><strong :class="securityKey ? 'is-ok' : 'is-err'">{{ securityKey ? '✓' : '✗' }}</strong></li>
            </ul>
          </div>
        </aside>
      </div>

      <!-- Preview modal -->
      <Transition name="lf-modal">
        <div v-if="showPreviewModal" class="lf-modal" @click.self="showPreviewModal = false">
          <div class="lf-modal__panel">
            <header>
              <h3>Vista previa</h3>
              <button @click="showPreviewModal = false">✕</button>
            </header>
            <iframe :srcdoc="emailPreview" class="lf-modal__iframe"></iframe>
          </div>
        </div>
      </Transition>

      <!-- Confirm modal -->
      <Transition name="lf-modal">
        <div v-if="confirmState.show" class="lf-modal" @click.self="closeConfirm">
          <div class="lf-modal__panel lf-modal__panel--sm">
            <header>
              <h3>{{ confirmState.title }}</h3>
            </header>
            <p>{{ confirmState.message }}</p>
            <div class="lf-modal__actions">
              <button class="lf-btn lf-btn--ghost" @click="closeConfirm">Cancelar</button>
              <button class="lf-btn lf-btn--primary" @click="resolveConfirm">Confirmar</button>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Toast -->
      <Transition name="lf-toast">
        <div v-if="toast.show" class="lf-toast" :class="`lf-toast--${toast.type}`">
          {{ toast.message }}
        </div>
      </Transition>

    </div>
  </div>
</template>

<script>
export default {
  name: 'BulkEmailCampaign',

  data() {
    return {
      currentStep: 1,
      steps: [
        { id: 1, label: 'Contactos' },
        { id: 2, label: 'Plantilla' },
        { id: 3, label: 'Configuración' },
        { id: 4, label: 'Enviar' }
      ],

      contacts: [],
      emailText: '',
      uploading: false,
      currentPage: 1,
      pageSize: 10,
      tempSecurityKey: '',

      templates: [],
      selectedTemplate: null,
      selectedCategory: 'all',
      templateSearch: '',
      templateCategories: [
        { id: 'all', name: 'Todas' },
        { id: 'empresas', name: 'Empresas' },
        { id: 'pymes', name: 'PYMES' },
        { id: 'profesionales', name: 'Profesionales' },
        { id: 'condominios', name: 'Condominios' }
      ],

      emailSubject: '',
      customMessage: '',
      sendDelay: 2000,
      batchSize: 10,
      skipDuplicateCheck: false,

      securityKey: '',
      showSecurityKey: false,

      sending: false,
      progress: 0,
      sendingStats: { successful: 0, failed: 0 },
      sendingTime: '0s',
      campaignResults: null,

      smtpStatus: false,

      emailPreview: '',
      showPreviewModal: false,

      // Modal de confirmación reutilizable
      confirmState: {
        show: false,
        title: '',
        message: '',
        onConfirm: null
      },

      // Toast reactivo
      toast: {
        show: false,
        message: '',
        type: 'info'
      },
      toastTimer: null,

      // Categorías por defecto (fallback si el backend no las envía)
      defaultCategoryMap: {
        'pymes': 'pymes',
        'profesionales': 'profesionales',
        'condominio-completo': 'condominios',
        'condominio-finanzas': 'condominios',
        'condominio-acceso': 'condominios',
        'condominio-reservas': 'condominios',
        'condominio-asambleas': 'condominios',
        'condominio-multas': 'condominios'
      }
    }
  },

  computed: {
    displayedContacts() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.contacts.slice(start, start + this.pageSize)
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.contacts.length / this.pageSize))
    },

    selectedTemplateName() {
      const t = this.templates.find(t => t.id === this.selectedTemplate)
      return t ? t.name : ''
    },

    defaultSubject() {
      const t = this.templates.find(t => t.id === this.selectedTemplate)
      return t ? t.subject : ''
    },

    validContactsCount() {
      return this.contacts.filter(c => c.isValid).length
    },

    estimatedTime() {
      const n = this.validContactsCount
      if (!n) return '0s'
      const seconds = Math.round((n * this.sendDelay) / 1000)
      if (seconds < 60) return `${seconds}s`
      const m = Math.floor(seconds / 60)
      const s = seconds % 60
      return `${m}m ${s}s`
    },

    apiBaseUrl() {
      const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname)
      return isLocal ? 'http://localhost:4000/api/bulkMailRoutes' : '/api/bulkMailRoutes'
    },

    filteredTemplates() {
      let list = this.templates

      if (this.selectedCategory !== 'all') {
        list = list.filter(t => this.getTemplateCategory(t) === this.selectedCategory)
      }

      if (this.templateSearch) {
        const q = this.templateSearch.toLowerCase()
        list = list.filter(t =>
          (t.name || '').toLowerCase().includes(q) ||
          (t.description || '').toLowerCase().includes(q) ||
          (t.subject || '').toLowerCase().includes(q)
        )
      }

      return list
    }
  },

  mounted() {
    this.loadTemplates()
    this.checkSMTP()
  },

  methods: {
    // ========== NAV ==========
    canGoToStep(step) {
      if (step === 2) return this.contacts.length > 0
      if (step === 3) return !!this.selectedTemplate
      if (step === 4) return !!this.emailSubject && !!this.securityKey
      return true
    },

    goToStep(step) {
      if (!this.canGoToStep(step)) return
      this.currentStep = step
    },

    nextStep() {
      if (this.currentStep < 4) this.currentStep++
    },

    prevStep() {
      if (this.currentStep > 1) this.currentStep--
    },

    // ========== TOAST ==========
    showToast(message, type = 'info') {
      clearTimeout(this.toastTimer)
      this.toast = { show: true, message, type }
      this.toastTimer = setTimeout(() => {
        this.toast.show = false
      }, 3200)
    },

    // ========== CONFIRM MODAL ==========
    ask(title, message, onConfirm) {
      this.confirmState = { show: true, title, message, onConfirm }
    },
    closeConfirm() {
      this.confirmState.show = false
    },
    resolveConfirm() {
      const fn = this.confirmState.onConfirm
      this.confirmState.show = false
      if (typeof fn === 'function') fn()
    },

    askClearContacts() {
      this.ask(
        'Eliminar contactos',
        `¿Eliminar ${this.contacts.length} contactos? Esta acción no se puede deshacer.`,
        () => {
          this.contacts = []
          this.showToast('Contactos eliminados', 'info')
        }
      )
    },

    askStartSending() {
      this.ask(
        'Confirmar envío',
        `¿Enviar ${this.validContactsCount} emails usando la plantilla "${this.selectedTemplateName}"?`,
        () => this.startSending()
      )
    },

    askStartNewCampaign() {
      this.ask(
        'Nueva campaña',
        '¿Iniciar una nueva campaña? Se limpiarán todos los datos actuales.',
        () => {
          this.currentStep = 1
          this.contacts = []
          this.selectedTemplate = null
          this.emailSubject = ''
          this.customMessage = ''
          this.securityKey = ''
          this.tempSecurityKey = ''
          this.campaignResults = null
          this.progress = 0
          this.selectedCategory = 'all'
          this.templateSearch = ''
          this.showToast('Nueva campaña iniciada', 'info')
        }
      )
    },

    // ========== CONTACTOS ==========
    triggerCSVUpload() {
      this.$refs.csvInput.click()
    },

    async handleCSVUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      this.uploading = true
      const formData = new FormData()
      formData.append('csvFile', file)
      formData.append('securityKey', this.tempSecurityKey)

      try {
        const res = await fetch(`${this.apiBaseUrl}/upload-csv`, {
          method: 'POST',
          body: formData
        })
        const data = await res.json()

        if (data.success) {
          const newContacts = data.contacts.map(c => ({
            ...c,
            isValid: this.validateEmail(c.email)
          }))
          this.contacts = [...this.contacts, ...newContacts]
          this.showToast(`${data.stats.valid} contactos cargados`, 'success')
        } else {
          this.showToast('Error: ' + data.error, 'error')
        }
      } catch {
        this.showToast('Error al subir archivo', 'error')
      } finally {
        this.uploading = false
      }
    },

    async processEmailText() {
      if (!this.emailText.trim()) return

      try {
        const res = await fetch(`${this.apiBaseUrl}/process-text`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            emailsText: this.emailText,
            securityKey: this.tempSecurityKey
          })
        })
        const data = await res.json()

        if (data.success) {
          const newContacts = data.contacts.map(c => ({
            ...c,
            isValid: this.validateEmail(c.email)
          }))
          this.contacts = [...this.contacts, ...newContacts]
          this.emailText = ''
          this.showToast(`${data.stats.valid} contactos cargados`, 'success')
        } else {
          this.showToast('Error: ' + data.error, 'error')
        }
      } catch {
        this.showToast('Error al procesar emails', 'error')
      }
    },

    validateEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    },

    // ========== PAGINACIÓN ==========
    nextPage() { if (this.currentPage < this.totalPages) this.currentPage++ },
    prevPage() { if (this.currentPage > 1) this.currentPage-- },
    goToPage(p) { this.currentPage = p },

    // ========== PLANTILLAS ==========
    async loadTemplates() {
      try {
        const res = await fetch(`${this.apiBaseUrl}/templates`)
        const data = await res.json()
        if (data.success) this.templates = data.templates
      } catch (e) {
        console.error('Error cargando plantillas:', e)
      }
    },

    selectTemplate(id) {
      this.selectedTemplate = id
      this.emailSubject = this.defaultSubject
      this.showToast('Plantilla seleccionada', 'success')
    },

    async previewTemplate(id) {
      try {
        const res = await fetch(`${this.apiBaseUrl}/preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            templateId: id,
            nombre: 'Cliente Ejemplo',
            empresa: 'Empresa Ejemplo',
            telefono: '+56 9 1234 5678',
            fecha: new Date().toLocaleDateString('es-CL'),
            customMessage: this.customMessage
          })
        })
        const data = await res.json()
        if (data.success) {
          this.emailPreview = data.preview
          this.showPreviewModal = true
        }
      } catch {
        this.showToast('Error al generar vista previa', 'error')
      }
    },

    getTemplateCategory(template) {
      // Prioridad 1: categoría enviada por el backend
      if (template && template.category) return template.category
      // Fallback: mapa por ID
      return this.defaultCategoryMap[template?.id] || 'empresas'
    },

    getCategoryCount(categoryId) {
      if (categoryId === 'all') return this.templates.length
      return this.templates.filter(t => this.getTemplateCategory(t) === categoryId).length
    },

    clearFilters() {
      this.selectedCategory = 'all'
      this.templateSearch = ''
    },

    // ========== ENVÍO ==========
    async sendTestEmail() {
      if (!this.securityKey) {
        this.showToast('Ingresa la clave de seguridad primero', 'warning')
        return
      }

      const testEmail = window.prompt('Email para la prueba:')
      if (!testEmail || !this.validateEmail(testEmail)) {
        this.showToast('Email inválido', 'warning')
        return
      }

      try {
        const res = await fetch(`${this.apiBaseUrl}/test-send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            templateId: this.selectedTemplate,
            testEmail,
            customSubject: this.emailSubject,
            customMessage: this.customMessage,
            securityKey: this.securityKey
          })
        })
        const data = await res.json()
        if (data.success) this.showToast('Email de prueba enviado', 'success')
        else this.showToast('Error: ' + data.error, 'error')
      } catch {
        this.showToast('Error al enviar prueba', 'error')
      }
    },

    async checkDuplicates() {
      if (!this.securityKey) {
        this.showToast('Ingresa la clave de seguridad primero', 'warning')
        return
      }

      try {
        const validContacts = this.contacts.filter(c => c.isValid)
        const res = await fetch(`${this.apiBaseUrl}/check-duplicates`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contacts: validContacts,
            securityKey: this.securityKey,
            daysLimit: 30
          })
        })
        const data = await res.json()
        if (data.success) {
          const n = data.duplicates?.duplicates || 0
          this.showToast(n > 0 ? `${n} duplicados encontrados` : 'Sin duplicados', n > 0 ? 'warning' : 'success')
        }
      } catch {
        this.showToast('Error al verificar duplicados', 'error')
      }
    },

    async startSending() {
      if (!this.securityKey) return this.showToast('Falta la clave', 'warning')
      if (!this.validContactsCount) return this.showToast('Sin contactos válidos', 'warning')

      this.sending = true
      this.progress = 0
      this.sendingStats = { successful: 0, failed: 0 }
      this.campaignResults = null

      const validContacts = this.contacts.filter(c => c.isValid)
      const startTime = Date.now()

      try {
        const res = await fetch(`${this.apiBaseUrl}/send-bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contacts: validContacts,
            templateId: this.selectedTemplate,
            customSubject: this.emailSubject,
            customMessage: this.customMessage,
            delayBetweenEmails: this.sendDelay,
            batchSize: this.batchSize,
            skipDuplicateCheck: this.skipDuplicateCheck,
            securityKey: this.securityKey
          })
        })
        const data = await res.json()

        if (data.success) {
          const stats = {
            successful: data.statistics?.successful || 0,
            failed: data.statistics?.failed || 0
          }
          this.campaignResults = stats
          this.sendingStats = stats
          this.progress = 100
          this.sendingTime = `${Math.round((Date.now() - startTime) / 1000)}s`
          this.showToast('Campaña enviada', 'success')
        } else {
          this.showToast('Error: ' + data.error, 'error')
        }
      } catch (e) {
        this.showToast('Error: ' + e.message, 'error')
      } finally {
        this.sending = false
      }
    },

    // ========== UTILS ==========
    async checkSMTP() {
      try {
        const res = await fetch(`${this.apiBaseUrl}/health`)
        const data = await res.json()
        this.smtpStatus = data.services?.smtp || false
      } catch { /* silencioso */ }
    },

    async testSMTPConnection() {
      if (!this.securityKey) {
        this.showToast('Ingresa la clave de seguridad primero', 'warning')
        return
      }
      try {
        const res = await fetch(`${this.apiBaseUrl}/test-smtp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ securityKey: this.securityKey })
        })
        const data = await res.json()
        this.smtpStatus = !!data.success
        this.showToast(data.success ? 'SMTP OK' : 'Error SMTP', data.success ? 'success' : 'error')
      } catch {
        this.showToast('Error al probar SMTP', 'error')
      }
    },

    async downloadTemplate() {
      try {
        const res = await fetch(`${this.apiBaseUrl}/download-template`)
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'template-contactos.csv'
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
        this.showToast('Template descargado', 'success')
      } catch {
        this.showToast('Error al descargar', 'error')
      }
    }
  },

  beforeUnmount() {
    clearTimeout(this.toastTimer)
  }
}
</script>

<style scoped>
/* =========================================================
   BulkEmail — estilos alineados al sistema LaFuerza
   ========================================================= */

.lf-bulk {
  min-height: 100vh;
  background: var(--lf-bg);
  color: var(--lf-text);
  padding-top: 100px;
}

/* Header */
.lf-bulk__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid var(--lf-border);
}
.lf-bulk__header h1 {
  font-family: var(--lf-font-display);
  font-size: 1.8rem;
  margin: 0 0 4px;
}
.lf-bulk__header p {
  margin: 0;
  color: var(--lf-text-mute);
  font-size: 14px;
}

.lf-bulk__stats {
  display: flex;
  gap: 24px;
}
.lf-stat {
  text-align: right;
}
.lf-stat__val {
  display: block;
  font-family: var(--lf-font-display);
  font-size: 1.4rem;
  font-weight: 600;
}
.lf-stat__val.is-ok { color: var(--lf-accent); }
.lf-stat__val.is-err { color: #f87171; }
.lf-stat__label {
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--lf-text-dim);
}

/* Steps nav */
.lf-steps {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  padding: 8px;
  background: var(--lf-surface);
  border: 1px solid var(--lf-border);
  border-radius: 12px;
  overflow-x: auto;
}
.lf-steps__btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  color: var(--lf-text-mute);
  font-family: var(--lf-font-body);
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}
.lf-steps__btn:hover:not(:disabled) { color: var(--lf-text); }
.lf-steps__btn.is-active {
  background: rgba(0, 229, 255, 0.08);
  border-color: rgba(0, 229, 255, 0.3);
  color: var(--lf-accent);
}
.lf-steps__btn:disabled { opacity: 0.4; cursor: not-allowed; }
.lf-steps__num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px; height: 22px;
  border-radius: 50%;
  background: rgba(255,255,255,0.05);
  font-size: 11.5px;
  font-weight: 600;
}
.lf-steps__btn.is-active .lf-steps__num {
  background: var(--lf-accent);
  color: #001014;
}

/* Panel */
.lf-panel {
  background: var(--lf-surface);
  border: 1px solid var(--lf-border);
  border-radius: 14px;
  padding: 28px;
}
.lf-panel__title {
  font-family: var(--lf-font-display);
  font-size: 1.3rem;
  margin: 0 0 20px;
}
.lf-panel__subhead {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.lf-panel__subhead h3 { font-size: 1rem; margin: 0; }
.lf-panel__section { margin-top: 28px; }
.lf-panel__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--lf-border);
}
.lf-panel__actions--split { justify-content: space-between; }
.lf-actions-right { display: flex; gap: 10px; }

/* Cards */
.lf-card-sm {
  background: rgba(255,255,255,0.02);
  border: 1px solid var(--lf-border);
  border-radius: 12px;
  padding: 20px;
  height: 100%;
}
.lf-card-sm h3 { font-size: 1rem; margin: 0 0 6px; }
.lf-hint {
  font-size: 12.5px;
  color: var(--lf-text-dim);
  margin: 0 0 12px;
}

/* Inputs */
.lf-input {
  display: block;
  width: 100%;
  padding: 10px 12px;
  background: rgba(255,255,255,0.03);
  border: 1px solid var(--lf-border);
  border-radius: 8px;
  color: var(--lf-text);
  font-family: var(--lf-font-body);
  font-size: 14px;
  transition: border-color 0.2s, background 0.2s;
  outline: none;
  margin-bottom: 10px;
}
.lf-input:focus {
  border-color: var(--lf-accent);
  background: rgba(0,229,255,0.03);
}
.lf-input--file {
  padding: 8px;
  font-size: 13px;
}
.lf-field { display: block; margin-bottom: 16px; }
.lf-field > span {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: var(--lf-text-mute);
  margin-bottom: 6px;
}
.lf-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.lf-input-group {
  display: flex;
  gap: 8px;
}
.lf-input-group .lf-input { margin-bottom: 0; }

/* Checkbox */
.lf-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: var(--lf-text-mute);
  margin-bottom: 16px;
  cursor: pointer;
}
.lf-checkbox input { accent-color: var(--lf-accent); }

/* Buttons */
.lf-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-family: var(--lf-font-body);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}
.lf-btn--primary {
  background: var(--lf-accent);
  color: #001014;
}
.lf-btn--primary:hover:not(:disabled) {
  background: #fff;
  box-shadow: 0 0 20px rgba(0,229,255,0.3);
}
.lf-btn--ghost {
  background: transparent;
  border-color: var(--lf-border-2);
  color: var(--lf-text);
}
.lf-btn--ghost:hover:not(:disabled) {
  border-color: var(--lf-accent);
  color: var(--lf-accent);
}
.lf-btn--danger {
  background: rgba(248,113,113,0.1);
  border-color: rgba(248,113,113,0.3);
  color: #f87171;
}
.lf-btn--danger:hover { background: rgba(248,113,113,0.2); }
.lf-btn--full { width: 100%; margin-bottom: 8px; }
.lf-btn--sm { padding: 6px 12px; font-size: 13px; }
.lf-btn--icon { padding: 10px 14px; }
.lf-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* Table */
.lf-table-wrap {
  border: 1px solid var(--lf-border);
  border-radius: 10px;
  overflow: auto;
  max-height: 400px;
}
.lf-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
}
.lf-table th,
.lf-table td {
  padding: 10px 14px;
  text-align: left;
  border-bottom: 1px solid var(--lf-border);
}
.lf-table th {
  background: rgba(255,255,255,0.02);
  font-size: 11.5px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--lf-text-dim);
  font-weight: 600;
  position: sticky;
  top: 0;
}
.lf-table td { color: var(--lf-text-mute); }
.lf-table tr:last-child td { border-bottom: none; }
.lf-table code {
  font-family: var(--lf-font-display);
  color: var(--lf-accent);
  font-size: 12.5px;
}

.lf-dot {
  display: inline-block;
  width: 8px; height: 8px;
  border-radius: 50%;
}
.lf-dot.is-ok { background: #4ade80; box-shadow: 0 0 8px rgba(74,222,128,0.5); }
.lf-dot.is-err { background: #f87171; }

/* Pagination */
.lf-pagination {
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-top: 14px;
}
.lf-pagination button {
  min-width: 34px;
  height: 34px;
  padding: 0 10px;
  background: transparent;
  border: 1px solid var(--lf-border);
  border-radius: 6px;
  color: var(--lf-text-mute);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}
.lf-pagination button:hover:not(:disabled) {
  border-color: var(--lf-accent);
  color: var(--lf-accent);
}
.lf-pagination button.is-active {
  background: var(--lf-accent);
  border-color: var(--lf-accent);
  color: #001014;
  font-weight: 600;
}
.lf-pagination button:disabled { opacity: 0.3; cursor: not-allowed; }

/* Filters */
.lf-filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.lf-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  background: transparent;
  border: 1px solid var(--lf-border);
  border-radius: 999px;
  color: var(--lf-text-mute);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}
.lf-chip:hover { color: var(--lf-text); border-color: var(--lf-border-2); }
.lf-chip.is-active {
  background: rgba(0,229,255,0.08);
  border-color: rgba(0,229,255,0.4);
  color: var(--lf-accent);
}
.lf-chip__count {
  padding: 1px 7px;
  border-radius: 999px;
  background: rgba(255,255,255,0.06);
  font-size: 11.5px;
}

/* Template cards */
.lf-templates {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 16px;
}
.lf-template {
  background: rgba(255,255,255,0.02);
  border: 1px solid var(--lf-border);
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
}
.lf-template:hover { border-color: var(--lf-border-2); }
.lf-template.is-selected {
  border-color: var(--lf-accent);
  background: rgba(0,229,255,0.04);
}
.lf-template header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
}
.lf-template h4 { font-size: 0.95rem; margin: 0; }
.lf-template__check { color: var(--lf-accent); font-weight: 600; }
.lf-template p {
  font-size: 13px;
  color: var(--lf-text-mute);
  margin: 0 0 8px;
  line-height: 1.5;
}
.lf-template__subject { font-size: 12.5px; }
.lf-template__subject strong { color: var(--lf-text-dim); font-weight: 500; }

/* Empty */
.lf-empty {
  text-align: center;
  padding: 40px 20px;
  color: var(--lf-text-mute);
}
.lf-empty p { margin: 0 0 6px; }

/* Summary */
.lf-summary {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 16px;
  background: rgba(0,229,255,0.04);
  border-left: 3px solid var(--lf-accent);
  border-radius: 8px;
  margin-bottom: 20px;
}
.lf-summary strong { font-size: 14.5px; }
.lf-summary small { color: var(--lf-text-mute); font-size: 12.5px; }

/* Warn box */
.lf-warn {
  margin-top: 20px;
  padding: 16px 18px;
  background: rgba(251,191,36,0.06);
  border: 1px solid rgba(251,191,36,0.25);
  border-radius: 10px;
}
.lf-warn strong { display: block; margin-bottom: 6px; font-size: 14px; }
.lf-warn .lf-hint { margin-bottom: 12px; }
.lf-warn code {
  font-family: var(--lf-font-display);
  padding: 1px 6px;
  background: rgba(0,0,0,0.3);
  border-radius: 4px;
  font-size: 12px;
}

/* KPIs */
.lf-kpis {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}
.lf-kpis > div {
  padding: 14px;
  background: rgba(255,255,255,0.02);
  border: 1px solid var(--lf-border);
  border-radius: 10px;
  text-align: center;
}
.lf-kpis strong {
  display: block;
  font-family: var(--lf-font-display);
  font-size: 1.3rem;
  color: var(--lf-text);
  margin-bottom: 4px;
}
.lf-kpis span {
  font-size: 11.5px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--lf-text-dim);
}

/* Progress */
.lf-progress {
  margin-bottom: 20px;
}
.lf-progress__bar {
  height: 8px;
  background: linear-gradient(90deg, var(--lf-accent), #00b8d4);
  border-radius: 999px;
  transition: width 0.3s ease;
}
.lf-progress p {
  margin: 8px 0 0;
  font-size: 13px;
  color: var(--lf-text-mute);
}

/* Success */
.lf-success {
  padding: 16px 18px;
  background: rgba(74,222,128,0.06);
  border: 1px solid rgba(74,222,128,0.25);
  border-radius: 10px;
  margin-bottom: 20px;
}
.lf-success strong { display: block; margin-bottom: 4px; }
.lf-success p { margin: 0; color: var(--lf-text-mute); font-size: 13.5px; }

/* Sidebar */
.lf-side {
  background: var(--lf-surface);
  border: 1px solid var(--lf-border);
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 16px;
}
.lf-side h3 {
  font-family: var(--lf-font-display);
  font-size: 0.95rem;
  margin: 0 0 14px;
}
.lf-side__list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.lf-side__list li {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--lf-border);
  font-size: 13.5px;
}
.lf-side__list li:last-child { border-bottom: none; }
.lf-side__list span { color: var(--lf-text-mute); }
.lf-side__list strong { color: var(--lf-text); }
.lf-side__list .is-ok { color: #4ade80; }
.lf-side__list .is-err { color: #f87171; }

/* Modal */
.lf-modal {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 2000;
}
.lf-modal__panel {
  background: var(--lf-surface);
  border: 1px solid var(--lf-border-2);
  border-radius: 16px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px;
}
.lf-modal__panel--sm { max-width: 440px; }
.lf-modal__panel header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.lf-modal__panel header h3 { font-size: 1.15rem; margin: 0; }
.lf-modal__panel header button {
  background: transparent;
  border: 1px solid var(--lf-border);
  color: var(--lf-text-mute);
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
}
.lf-modal__iframe {
  width: 100%;
  height: 60vh;
  border: 1px solid var(--lf-border);
  border-radius: 10px;
  background: #fff;
}
.lf-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

/* Toast */
.lf-toast {
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 22px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  z-index: 3000;
  border: 1px solid;
  backdrop-filter: blur(8px);
}
.lf-toast--success { background: rgba(74,222,128,0.15); border-color: rgba(74,222,128,0.4); color: #86efac; }
.lf-toast--error   { background: rgba(248,113,113,0.15); border-color: rgba(248,113,113,0.4); color: #fca5a5; }
.lf-toast--warning { background: rgba(251,191,36,0.15); border-color: rgba(251,191,36,0.4); color: #fcd34d; }
.lf-toast--info    { background: rgba(0,229,255,0.15); border-color: rgba(0,229,255,0.4); color: var(--lf-accent); }

.lf-toast-enter-active,
.lf-toast-leave-active { transition: opacity 0.25s, transform 0.25s; }
.lf-toast-enter-from,
.lf-toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(12px); }

/* Modal transition */
.lf-modal-enter-active,
.lf-modal-leave-active { transition: opacity 0.2s; }
.lf-modal-enter-from,
.lf-modal-leave-to { opacity: 0; }

/* Responsive */
@media (max-width: 768px) {
  .lf-bulk { padding-top: 90px; }
  .lf-bulk__header { flex-direction: column; align-items: flex-start; }
  .lf-bulk__stats { gap: 16px; }
  .lf-panel { padding: 20px; }
  .lf-templates { grid-template-columns: 1fr; }
  .lf-kpis { grid-template-columns: 1fr 1fr; }
  .lf-row-2 { grid-template-columns: 1fr; }
  .lf-steps { flex-wrap: nowrap; }
  .lf-steps__btn { padding: 10px 12px; font-size: 12.5px; }
  .lf-steps__btn span:not(.lf-steps__num) { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .lf-toast-enter-active,
  .lf-toast-leave-active,
  .lf-modal-enter-active,
  .lf-modal-leave-active { transition: none; }
}
</style>
